import React, {useState, useRef, useCallback} from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  useMediaQuery,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {PhotoCamera, Security, AccessTime, CameraAlt, CheckCircle, ErrorOutline} from '@mui/icons-material';
import Webcam from 'react-webcam';
import {BASE_URL} from '../../api/constants';
import AimdFlowQuestionBase from '../../components/aimdFlowQuestionBase';
import {useNavigate} from 'react-router-dom';

const DermatologyScreening = () => {
  const isLaptop = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [currentStep, setCurrentStep] = useState('camera');
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();

  const webcamRef = useRef(null);

  // Video constraints for better quality
  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: 'user',
    deviceId: selectedCameraId ? {exact: selectedCameraId} : undefined,
  };

  // Get available cameras
  const getAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);

      if (videoDevices.length > 0 && !selectedCameraId) {
        const frontCamera = videoDevices.find(
          d =>
            d.label.toLowerCase().includes('front') ||
            d.label.toLowerCase().includes('user') ||
            d.label.toLowerCase().includes('facetime'),
        );
        const defaultCamera = frontCamera || videoDevices[0];
        setSelectedCameraId(defaultCamera.deviceId);
      }
    } catch (error) {
      console.error('Error getting cameras:', error);
      setCameraError('Unable to access camera devices. Please check permissions.');
    }
  }, [selectedCameraId]);

  // Handle camera selection change
  const handleCameraChange = event => {
    setSelectedCameraId(event.target.value);
    setCameraReady(false);
  };

  // Convert base64 to blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], {type: mimeType});
  };

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!webcamRef.current || !cameraReady) {
      setCameraError('Camera not ready for capture.');
      return;
    }

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        const blob = base64ToBlob(imageSrc, 'image/jpeg');
        setCapturedImage({
          blob: blob,
          dataUrl: imageSrc,
        });
        setCurrentStep('captured');
      } else {
        setCameraError('Failed to capture image. Please try again.');
      }
    } catch (error) {
      console.error('Capture error:', error);
      setCameraError('Failed to capture photo. Please try again.');
    }
  }, [cameraReady]);

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setApiError('');
    setCurrentStep('camera');
    setCameraReady(false);
    setCameraError('');
  }, []);

  // Process photo with API call
  const processPhoto = async () => {
    if (!capturedImage) return;

    setIsProcessing(true);
    setApiError('');

    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append('file', capturedImage.blob, 'capture.jpeg');

      // Make API call
      const response = await fetch(`${BASE_URL}/skyn/result`, {
        method: 'POST',
        body: formData,
        headers: {
          accept: 'application/json',
        },
      });

      const result = await response.json();

      // Check for Internal Server Error response
      if (result.message === 'Internal Server Error' || !response.ok) {
        setApiError(
          'The photo quality was not good enough for analysis. Please retake the photo with better lighting and positioning.',
        );
        return;
      }

      // Store response in localStorage
      localStorage.setItem('skynResult', JSON.stringify(result));

      // Navigate to pre-face-scan page using window.location
      navigate('/pre-face-scan', {
        state: {
          isComingFromDemographicsPage: true,
        },
      });
    } catch (error) {
      console.error('API Error:', error);

      // Check if the error is specifically Internal Server Error
      if (error.message.includes('Internal Server Error') || error.message.includes('500')) {
        setApiError(
          'The photo quality was not good enough for analysis. Please retake the photo with better lighting and positioning.',
        );
      } else {
        setApiError(`Failed to process image: ${error.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Webcam event handlers
  const onUserMedia = () => {
    console.log('Camera started successfully');
    setCameraReady(true);
    setCameraError('');
  };

  const onUserMediaError = error => {
    console.error('Camera error:', error);
    setCameraReady(false);

    let errorMessage = 'Camera access failed. ';
    if (error.name === 'NotAllowedError') {
      errorMessage += 'Please allow camera permissions and refresh the page.';
    } else if (error.name === 'NotFoundError') {
      errorMessage += 'No camera found. Please check your camera connection.';
    } else if (error.name === 'NotReadableError') {
      errorMessage += 'Camera is being used by another application.';
    } else {
      errorMessage += 'Please check your camera and try again.';
    }

    setCameraError(errorMessage);
  };

  const getProgressValue = () => {
    if (currentStep === 'camera') return 50;
    if (currentStep === 'captured') return 75;
    return 100;
  };

  // Camera Screen Content
  const CameraContent = () => (
    <Stack
      spacing={2} // Reduced from 3
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        px: {xs: 2, sm: 3, md: 4},
        py: {xs: 1, sm: 2}, // Reduced padding
        maxWidth: {xs: '100%', sm: '500px', lg: '800px'},
        width: '100%',
        height: '100%', // Take full available height
        maxHeight: 'calc(100vh - 70px)', // Account for header/footer space
        overflow: 'hidden', // Prevent any overflow
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: '#000',
          fontSize: {xs: '1.1rem', sm: '1.25rem', md: '1.5rem'}, // Slightly smaller
          mb: 0.5,
        }}
      >
        Lets first analyze your skin health.
      </Typography>

      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.4, // Reduced line height
          fontSize: {xs: '0.8rem', sm: '0.85rem'}, // Smaller font
          color: '#000',
          width: '100%',
          mb: 2,
        }}
      >
        With a clear facial image, we will be able to assess and recommend products to maintain and improve your skin
        health. Please enable your device camera and take a photo of your face.{' '}
      </Typography>

      {/* Tips */}
      <Stack
        direction={{xs: 'row', sm: 'row'}}
        spacing={1}
        sx={{mb: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 1}}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            backgroundColor: '#EAF9F9',
            padding: '4px 8px', // Smaller padding
            borderRadius: '15px',
            minWidth: 'auto',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#35C6C9',
              fontWeight: 500,
              fontSize: '0.7rem', // Smaller font
            }}
          >
            Good lighting
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            backgroundColor: '#EAF9F9',
            padding: '4px 8px',
            borderRadius: '15px',
            minWidth: 'auto',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#35C6C9',
              fontWeight: 500,
              fontSize: '0.7rem',
            }}
          >
            No Filters
          </Typography>
        </Stack>
      </Stack>

      {/* Camera Selection Dropdown */}
      {availableCameras.length > 1 && (
        <FormControl size="small" sx={{minWidth: 180, mb: 1}}>
          <InputLabel sx={{fontSize: '0.8rem', color: '#35C6C9'}}>Camera</InputLabel>
          <Select
            value={selectedCameraId}
            onChange={handleCameraChange}
            label="Camera"
            sx={{
              borderRadius: '20px',
              height: '35px', // Fixed smaller height
              fontSize: '0.8rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#35C6C9',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#5bc5c7',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#35C6C9',
              },
            }}
          >
            {availableCameras.map((camera, index) => (
              <MenuItem key={camera.deviceId} value={camera.deviceId}>
                <Typography sx={{fontSize: '0.8rem'}}>{camera.label || `Camera ${index + 1}`}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Error Alert */}
      {cameraError && (
        <Alert
          severity="error"
          sx={{
            width: '100%',
            mb: 1,
            maxWidth: '400px',
            '& .MuiAlert-message': {
              fontSize: '0.8rem',
            },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              sx={{fontSize: '0.7rem'}}
              onClick={() => {
                setCameraError('');
                setCameraReady(false);
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        >
          {cameraError}
        </Alert>
      )}

      {/* Camera Preview*/}
      <Box
        sx={{
          position: 'relative',
          width: {xs: '200px', sm: '220px', md: '270px'}, // Smaller sizes
          height: {xs: '200px', sm: '220px', md: '270px'},
          borderRadius: '50%',
          overflow: 'hidden',
          border: '2px dashed #35C6C9', // Thinner border
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          flexShrink: 0, // Prevent shrinking
        }}
      >
        {!cameraError ? (
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMedia={onUserMedia}
            onUserMediaError={onUserMediaError}
            mirrored={true}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
            }}
          >
            <CameraAlt sx={{fontSize: 36, mb: 0.5}} />
            <Typography variant="body2" sx={{fontSize: '0.8rem'}}>
              Camera Error
            </Typography>
          </Box>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontSize: {xs: '0.75rem', sm: '0.9rem'},
          color: '#000',
          fontWeight: 500,
          mt: 0.5,
        }}
      >
        Position your face within the circle
      </Typography>

      {/* Security note */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{color: '#000'}}>
        <Security sx={{fontSize: 14}} />
        <Typography
          variant="caption"
          sx={{
            fontSize: {xs: '0.65rem', sm: '0.7rem'},
            textAlign: 'center',
            maxWidth: '400px',
          }}
        >
          Images are securely encrypted and only used for assessment
        </Typography>
      </Stack>

      {/* Capture Button - Always visible */}
      <Box sx={{mt: 'auto', pt: 1, flexShrink: 0}}>
        <Button
          variant="contained"
          size="medium" // Changed from large
          onClick={capturePhoto}
          disabled={!cameraReady || !!cameraError}
          sx={{
            backgroundColor: '#35C6C9',
            color: 'white',
            fontWeight: 600,
            fontSize: {xs: '0.85rem', sm: '0.9rem'},
            py: {xs: 1, sm: 1.2}, // Reduced padding
            px: {xs: 2.5, sm: 3},
            borderRadius: 50,
            textTransform: 'none',
            boxShadow: '0px 4px 12px rgba(114, 216, 218, 0.3)',
            minWidth: {xs: '130px', sm: '140px'},
            '&:hover': {
              backgroundColor: '#5bc5c7',
              boxShadow: '0px 6px 16px rgba(114, 216, 218, 0.4)',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666',
            },
          }}
        >
          <CameraAlt sx={{mr: 0.5, fontSize: 18}} />
          {cameraReady ? 'Capture' : 'Starting...'}
        </Button>
      </Box>
    </Stack>
  );

  // Photo Captured Screen Content
  const CapturedContent = () => (
    <Stack
      spacing={3}
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        px: {xs: 2, sm: 3, md: 4},
        py: {xs: 3, sm: 4},
        maxWidth: {xs: '100%', sm: '500px', lg: '800px'},
        width: '100%',
      }}
    >
      <CheckCircle sx={{fontSize: {xs: 40, sm: 48}, color: '#4CAF50'}} />
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: '#000',
          fontSize: {xs: '1.25rem', sm: '1.5rem', md: '1.75rem'},
        }}
      >
        Photo Captured!
      </Typography>

      {/* Preview of captured image */}
      {capturedImage && (
        <Box
          sx={{
            width: {xs: '200px', sm: '250px'},
            height: {xs: '200px', sm: '250px'},
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid #35C6C9',
          }}
        >
          <img
            src={capturedImage.dataUrl}
            alt="Captured face"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      )}

      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.6,
          fontSize: {xs: '0.85rem', sm: '0.9rem'},
          color: '#666',
          maxWidth: '300px',
        }}
      >
        Great! Your photo looks good. Ready to analyze?
      </Typography>

      {/* Show API Error if any */}
      {apiError && (
        <Alert severity="warning" sx={{width: '100%', mb: 2, maxWidth: '450px'}} icon={<ErrorOutline />}>
          {apiError}
        </Alert>
      )}

      <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{mt: 3, width: '100%', maxWidth: '300px'}}>
        <Button
          variant="outlined"
          onClick={retakePhoto}
          disabled={isProcessing}
          sx={{
            borderColor: '#35C6C9',
            color: '#35C6C9',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 25,
            textTransform: 'none',
            flex: {xs: 'none', sm: 1},
            '&:hover': {
              borderColor: '#5bc5c7',
              backgroundColor: 'rgba(53, 198, 201, 0.1)',
            },
          }}
        >
          Retake
        </Button>

        <Button
          variant="contained"
          onClick={processPhoto}
          disabled={isProcessing}
          sx={{
            backgroundColor: '#35C6C9',
            color: 'white',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 25,
            textTransform: 'none',
            minWidth: '120px',
            flex: {xs: 'none', sm: 1},
            '&:hover': {
              backgroundColor: '#5bc5c7',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={16} sx={{mr: 1, color: 'white'}} />
              Analyzing...
            </>
          ) : (
            'Analyze'
          )}
        </Button>
      </Stack>
    </Stack>
  );

  // Initialize camera on component mount
  React.useEffect(() => {
    if (currentStep === 'camera') {
      getAvailableCameras();
    }
  }, [currentStep, getAvailableCameras]);

  // Render content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'camera':
        return <CameraContent />;
      case 'captured':
        return <CapturedContent />;
      default:
        return <CameraContent />;
    }
  };

  return (
    <AimdFlowQuestionBase
      IsShowBackIcon={false}
      isShowMenuIcon={false}
      aimdLinearBarValue={getProgressValue()}
      isShowStepper={true}
      isBottomLogoVisible={true}
    >
      {renderContent()}
    </AimdFlowQuestionBase>
  );
};

export default DermatologyScreening;
